import { Box, Button, Card, Divider, Drawer, IconButton, Skeleton, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { useCallback, useEffect, useState } from "react";
import DialogComponent from "../../components/dialog/dialogComponent";
import SnackbarComponent from "../../components/snackBar/snackbarComponent";
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import InsertPhotoOutlinedIcon from '@mui/icons-material/InsertPhotoOutlined';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import CloseIcon from '@mui/icons-material/Close';
import WebCamera from "../../components/gallery/webCamera";
import ReactImagePickerEditor, { ImagePickerConf } from 'react-image-picker-editor';
import 'react-image-picker-editor/dist/index.css'
import { ButtonComponent } from "../../components/form";
import { useEditProfileDetailsMutation, useGetImageUploadURLMutation } from "../../services/profileApi";
import { useUploadToS3Mutation } from "../../services/uploadApi";
import {base64ToFile, getFileExtension} from '../../utils/customFunctions';
import { GetAuth } from "../../context/auth/authProvider";

const config2 = {
    borderRadius: '50%',
    language: 'en',
    width: '26px',
    height: '26px',
    objectFit: 'contain',
    compressInitial: null,
    darkMode: false,
    rtl: false,
    hideDeleteBtn: true,
    hideDownloadBtn: true,
    hideEditBtn: true,
    hideAddBtn: true
};

const ProfileImage = (url, width='100%', isLoading=false) => {
    return <Box
        sx={{
            position: 'relative',
            width: {width},
            p: 1,
            boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.2), 0 3px 10px 0 rgba(0, 0, 0, 0.19)',
            borderRadius: '50%'
        }}
    >
        <Card
            elevation={0}
            sx={{
                borderRadius: '50%',
                position: 'relative',
                overflow: 'hidden',
                aspectRatio: '1 / 1',
                width: '100%'
            }}
        >
            {
                isLoading
                ?
                <Skeleton variant="circular" width='100%' height='100%' />
                :
                <img 
                    src={url ?? "/images/default profile.png"}
                    alt="profile" 
                    width='100%'
                    height='100%'
                    style={{
                        borderRadius: '50%',
                        objectFit: 'cover',
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)'
                    }}
                />
            }
        </Card>
    </Box>
}

const ProfilePic = ({
    data,
    isLoading,
    loadLatestProfileDetails
}) => {
    const theme = useTheme();

    const auth = GetAuth();

    const [openImagePickerOption, setOpenImagePickerOption] = useState(false);
    const [openImagePickerDrawer, setOpenImagePickerDrawer] = useState(false);

    const [editType, setEditType] = useState(null);

    const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

    const changeProfilePicRequest = useCallback(() => {
        if(isMdUp){
            setOpenImagePickerOption(true);
        }
        else{
            setOpenImagePickerDrawer(true);
        }
    }, [isMdUp]);

    const [profileImageURL, setProfileImageURL] = useState(null);
    const [selectedImageURL, setSelectedImageURL] = useState(null);

    const [getUploadURL, { isLoading: getUploadURLLoading, error: getUploadURLError, data: getUploadURLData, isSuccess: getUploadURLSuccess }] = useGetImageUploadURLMutation();
    const [uploadToS3, { isLoading: getUploadS3Loading, error: uploadToS3Error, isSuccess: uploadToS3Success }] = useUploadToS3Mutation();

    useEffect(() => {
        if(data){
            setProfileImageURL(data.profileImg);
        }
    }, [data]);

    const [snackBar, setSnackBar] = useState({
        message: "",
        open: false,
        color: ""
    });

    const [
        editProfileData, 
        {
            isLoading: isLoadingEditProfileData,
            isSuccess: isSuccessEditProfileData,
            isError: isErrorEditProfileData,
            error: errorEditProfileData
        }
    ] = useEditProfileDetailsMutation();
        
    useEffect(() => {
        if(isSuccessEditProfileData){
            if(editType === "SAVE"){
                const newImg = selectedImageURL;

                setSelectedImageURL(null);

                setProfileImageURL(newImg);

                setOpenImagePickerOption(false);

                if(!isMdUp){
                    setOpenImagePickerDrawer(false);
                }

                setSnackBar({
                    message: "Profile image successfully updated",
                    open: true,
                    color: "green"
                });
            }
            else{
                setProfileImageURL(null);
                setSelectedImageURL(null);

                setSnackBar({
                    message: "Profile image successfully removed",
                    open: true,
                    color: "green"
                });
            }

            setEditType(null);

            loadLatestProfileDetails();
        }
        else if(isErrorEditProfileData && errorEditProfileData){
            setSnackBar({
                message: errorEditProfileData?.data?.message ?? "Something went wrong!",
                color: "red",
                open: true
            });
        }
    }, [isSuccessEditProfileData, isErrorEditProfileData, errorEditProfileData]);

    const callEditAPI = useCallback((imgURL) => {
        const body = {
            "phoneNo": data.phoneNo ?? data.contactNo,
            "email": data.email,
            "firstName": data.firstName,
            "lastName": data.lastName,
            "profileImg": imgURL,
            "role": Array.isArray(data?.roles) ? data.roles[0] : data?.role,
            "staffId": data.staffId
        }

        console.log(body);

        editProfileData({body: body});
    }, [data, getUploadURLData]);

    const savePhoto = useCallback(() => {
        setEditType("SAVE");
    }, []);

    const removePhoto = useCallback(() => {
        setEditType("DELETE");
    }, []);

    useEffect(() => {
        if(editType){
            if(editType === "DELETE"){
                callEditAPI(null);
            }
            else{
                console.log(selectedImageURL);
                
                const file = base64ToFile(selectedImageURL, 'profile');
                const extension = file.type.split('/')[1];

                getUploadURL({staffId: auth?.currentUser?.staffID, extension: extension, type: "PROFILE_IMG" });
            }
        }
    }, [editType]);

    useEffect(() => {
        if (getUploadURLSuccess && getUploadURLData) {
            console.log(getUploadURLData);

            const uploadData = getUploadURLData.data ?? getUploadURLData;
            const file = base64ToFile(selectedImageURL, 'profile');
            uploadToS3({ uploadUrl: uploadData.uploadUrl, file: file });
        }
        else if(getUploadURLError){
            console.log(getUploadURLError);

            setSnackBar({
                message: getUploadURLError?.data?.message ?? "Something went wrong!",
                open: true,
                color: "red"
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getUploadURLSuccess, getUploadURLData, getUploadURLError]);

    useEffect(() => {
        if (uploadToS3Success) {
            console.log(getUploadURLData);

            const uploadData = getUploadURLData.data ?? getUploadURLData;
            callEditAPI(uploadData.downloadUrl);
        }
        else if(uploadToS3Error){
            setSnackBar({
                message: uploadToS3Error?.data?.message ?? "Something went wrong!",
                open: true,
                color: "red"
            });
        }
    }, [uploadToS3Success, uploadToS3Error]);

    return (
        <>
            <Button
                onClick={changeProfilePicRequest}
                disableRipple
                sx={{
                    '&: hover': {
                        backgroundColor: 'white'
                    },
                    position: 'relative',
                    width: '100%',
                    p: {xs: 0, md: 6}
                }}
                disabled={isLoading}
            >
                {ProfileImage(profileImageURL, '100%', isLoading)}
                
                <CameraAltIcon 
                    sx={{
                        width: {xs: '20px', md: '41px'},
                        height: {xs: '18px', md: '38px'},
                        color: '#5E5959',
                        position: 'absolute',
                        top: {xs: '95%', md: '85%'},
                        right: {xs: '15%', md: '20%'},
                        transform: {xs: 'translate(-15%, -95%)', md: 'translate(-20%, -85%)'},
                        zIndex: 100
                    }}
                />
            </Button>

            {/* Profile photo options */}
            <DialogComponent 
                open={openImagePickerOption}
                title="Profile Photo"
                body={<>
                    <Stack
                        direction='column'
                        justifyContent='center'
                        alignItems='center'
                        width={500}
                        px={7}
                    >
                        {ProfileImage(selectedImageURL ?? profileImageURL, '50%')}

                        {
                            selectedImageURL
                            ?
                            <Stack
                                direction='row'
                                justifyContent='center'
                                alignItems='center'
                                width='100%'
                                pb={3}
                                pt={{xs: 0, md: 3}}
                                spacing={2}
                            >
                                <ButtonComponent 
                                    label="Cancel"
                                    onClick={() => setSelectedImageURL(null)}
                                    disabled={isLoadingEditProfileData || getUploadURLLoading || getUploadS3Loading}
                                />

                                <ButtonComponent 
                                    label="Save Photo"
                                    onClick={savePhoto}
                                    inLoading={isLoadingEditProfileData || getUploadURLLoading || getUploadS3Loading}
                                />
                            </Stack>
                            :
                            <ProfilePicOptionsList 
                                setProfileImageURL={setSelectedImageURL}
                                removePhoto={removePhoto}
                                isImageAvailable={profileImageURL || selectedImageURL}
                                setSnackBar={setSnackBar}
                            />
                        }
                    </Stack>
                </>}
                hideAction
                handleClose={() => {
                    setSelectedImageURL(null);
                    setOpenImagePickerOption(false);
                }}
                closeCallback={() => {
                    setSelectedImageURL(null);
                    setOpenImagePickerOption(false);
                }}
            />

            {/* Mobile view Profile photo confirmation */}
            <DialogComponent 
                open={!isMdUp && selectedImageURL}
                title="Profile Photo"
                body={<>
                    <Stack
                        direction='column'
                        justifyContent='center'
                        alignItems='center'
                        px={7}
                        spacing={10}
                    >
                        {ProfileImage(selectedImageURL, '100%')}

                        <Stack
                            direction='row'
                            justifyContent='center'
                            alignItems='center'
                            width='100%'
                            pb={3}
                            pt={{xs: 0, md: 3}}
                            spacing={2}
                        >
                            <ButtonComponent 
                                label="Cancel"
                                onClick={() => setSelectedImageURL(null)}
                                disabled={isLoadingEditProfileData || getUploadURLLoading || getUploadS3Loading}
                            />

                            <ButtonComponent 
                                label="Save Photo"
                                onClick={savePhoto}
                                inLoading={isLoadingEditProfileData || getUploadURLLoading || getUploadS3Loading}
                            />
                        </Stack>
                    </Stack>
                </>}
                hideAction
                handleClose={() => setOpenImagePickerOption(false)}
                fullScreen
            />

            <Drawer
                anchor='bottom'
                open={openImagePickerDrawer}
                onClose={() => setOpenImagePickerDrawer(false)}
                slotProps={{
                    paper: {
                        square: false,
                        style: {
                            borderRadius: '16px 16px 0px 0px',
                            paddingLeft: '30px',
                            paddingRight: '30px'
                        }
                    }
                }}
            >
                <Stack
                    direction='column'
                    justifyContent='center'
                    alignItems='center'
                    width='100%'
                >
                    <Stack
                        direction='row'
                        justifyContent='end'
                        alignItems='center'
                        width='100%'
                    >
                        <IconButton 
                            aria-label="delete" 
                            size="large"
                            onClick={() => setOpenImagePickerDrawer(false)}
                            sx={{
                                mt: 3,
                                p: 0
                            }}
                        >
                            <CloseIcon sx={{color: '#011627', width: 30, height: 30}}/>
                        </IconButton>
                    </Stack>

                    <Typography
                        fontWeight={500}
                        fontSize={20}
                        letterSpacing={0}
                        textAlign="center"
                        pt={1}
                        pb={2}
                    >
                        Profile Photo
                    </Typography>

                    <Divider sx={{width: '100%', mb: 3}}/>

                    <ProfilePicOptionsList 
                        setProfileImageURL={setSelectedImageURL}
                        removePhoto={removePhoto}
                        isImageAvailable={profileImageURL || selectedImageURL}
                        setSnackBar={setSnackBar}
                    />
                </Stack>
            </Drawer>

            <SnackbarComponent
                message={snackBar.message}
                color={snackBar.color}
                open={snackBar.open}
                handleClose={() => setSnackBar({
                    message: "",
                    open: false
                })}
            />
        </>
    );
}

export default ProfilePic;

const ProfilePicOptionsList = ({
    setProfileImageURL,
    removePhoto,
    isImageAvailable,
    setSnackBar
}) => {
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showCameraDialog, setShowCameraDialog] = useState(false);

    const openCamera = useCallback(() => {
        setShowCameraDialog(true);
    }, []);

    const deleteImage = useCallback(() => {
        setShowDeleteDialog(true);
    }, []);

    const confirmDeleteImage = useCallback(() => {
        setShowDeleteDialog(false);
        removePhoto();
    }, []);

    const onPhotoTaken = useCallback((imageUrl) => {
        setShowCameraDialog(false);
        setProfileImageURL(imageUrl);
    }, []);

    const onPhotoError = useCallback((error) => {
        setShowCameraDialog(false);

        console.log(error);

        setSnackBar({
            message: "The request is not allowed by the user agent or the platform in the current context, possibly because the user denied permission",
            open: true,
            color: "red"
        });
    }, []);

    const onPhotoStopped = useCallback(() => {
        // setShowCameraDialog(false);

        // setSnackBar({
        //     message: "Camera has been stopped",
        //     open: true,
        //     color: "yellow"
        // });
    }, []);

    return (
        <>
            <Stack
                direction='row'
                justifyContent='space-between'
                alignItems='center'
                width='100%'
                pb={3}
                pt={{xs: 0, md: 3}}
            >
                <Stack
                    direction='row'
                    justifyContent='start'
                    alignItems='center'
                    spacing={{xs: 4, md: 7}}
                >
                    <ProfilePicOption 
                        icon={<CameraAltOutlinedIcon sx={{color: '#000000', width: 26, height: 26}}/>}
                        label="Camera"
                        callback={openCamera}
                    />

                    <ProfilePicOption 
                        icon={
                            <>
                                <Box
                                    sx={{
                                        opacity: 0,
                                        zIndex: 1,
                                        maxWidth: '26px',
                                        maxHeight: '26px'
                                    }}
                                >
                                    <ReactImagePickerEditor 
                                        config={config2}
                                        imageSrcProp={undefined}
                                        imageChanged={(uri) => setProfileImageURL(uri)}
                                    />
                                </Box>

                                <InsertPhotoOutlinedIcon 
                                    sx={{
                                        color: '#000000', 
                                        width: 26, 
                                        height: 26,
                                        position: 'absolute'
                                    }}
                                />
                            </>
                        }
                        label="Gallery"
                    />
                </Stack>

                <IconButton 
                    aria-label="delete" 
                    size="small"
                    sx={{
                        p: 0,
                        '&: hover': {
                            backgroundColor: 'transparent'
                        }
                    }}
                    onClick={deleteImage}
                    disabled={!isImageAvailable}
                >
                    <DeleteForeverIcon sx={{color: '#5E5959', width: 30, height: 30}}/>
                </IconButton>
            </Stack>

            {/* Delete */}
            <DialogComponent 
                open={showDeleteDialog}
                title="Delete"
                body={<>
                    <Stack
                        direction='column'
                        justifyContent='center'
                        alignItems='center'
                    >
                        <Typography
                            fontWeight={300}
                            fontSize={14}
                            textAlign='justify'
                        >
                            Are you sure to delete your profile image?
                        </Typography>
                    </Stack>
                </>}
                handleClose={() => setShowDeleteDialog(false)}
                proceedText="No"
                cancelText="Yes"
                proceedCallback={() => setShowDeleteDialog(false)}
                cancelCallback={confirmDeleteImage}
            />

            {/* Camera */}
            <DialogComponent 
                open={showCameraDialog}
                title="Take Photo"
                body={<>
                    <WebCamera 
                        onPhotoTaken={onPhotoTaken}
                        onPhotoError={onPhotoError}
                        onPhotoStopped={onPhotoStopped}
                    />
                </>}
                closeCallback={() => setShowCameraDialog(false)}
                proceedText="Capture"
                proceedCallback={() => setShowCameraDialog(false)}
                hideAction
            />
        </>
    );
}

const ProfilePicOption = ({icon, label, callback}) => {
    return (
        <>
            <Stack
                direction='column'
                justifyContent='center'
                alignItems='center'
                spacing={1}
            >
                {
                    callback
                    ?
                    <IconButton 
                        aria-label="delete" 
                        size="small"
                        sx={{
                            p: 2,
                            backgroundColor: '#D9D9D9',
                            '&: hover': {
                                backgroundColor: '#D9D9D9'
                            }
                        }}
                        onClick={callback}
                    >
                        {icon}
                    </IconButton>
                    :
                    <Stack
                        sx={{
                            p: 2,
                            backgroundColor: '#D9D9D9',
                            borderRadius: '50%'
                        }}
                    >
                        {icon}
                    </Stack>
                }
                
                <Typography
                    fontWeight={400}
                    fontSize={{xs: 14, md: 16}}
                    letterSpacing={0}
                >
                    {label}
                </Typography>
            </Stack>
        </>
    );
}

